import React, { useState } from "react";
import ProgramsHierarchy from "@/shared/components/ProgramsHierarchy";
import { connect } from "react-redux";

import { Button, Col, Row, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import DatePicker from "react-datepicker";
import { CSVLink } from "react-csv";
import { getFirstDay } from "@/shared/helpers";
import { dateStrToYmd } from "@/shared/helpers";
import { isEqual, clone, cloneDeep } from "lodash";
import { CheckBoxField } from "@/shared/components/form/CheckBox";

const defaultFrom = getFirstDay();
const defaultTo = new Date();

const ScheduleAwardsSummaryFilter = ({
  filter,
  setFilter,
  setUseFilter,
  download,
  exportData,
  exportLink,
  exportHeaders,
  reportName,
  allColumns = [],
}) => {
  const options = {
    dateRange: true,
    programs: true,
    exportToCsv: true,
    onlyPending: true,
    viewAll: true,
    programId: true,
  };
  const [from, setFrom] = React.useState(defaultFrom);
  const [to, setTo] = React.useState(defaultTo);
  const [onlyPending, setOnlyPending] = React.useState(false);
  const [viewAll, setViewAll] = React.useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState(
    filter.programs ? filter.programs : []
  );
  const finalFilter = { ...filter };
  let previous = cloneDeep(finalFilter);

  const onClickFilter = (reset = false) => {
    let dataSet = {};
    if (options.onlyPending) {
      dataSet.onlyPending = reset ? false : onlyPending;
    }
    if (options.viewAll) {
      dataSet.viewAll = reset ? false : viewAll;
    }
    if (options.dateRange) {
      dataSet.from = dateStrToYmd(reset ? defaultFrom : from);
      dataSet.to = dateStrToYmd(reset ? defaultTo : to);
    }
    if (options.programs) {
      dataSet.programs = reset ? [] : clone(selectedPrograms);
    }
    if (options.programId) {
      dataSet.programId = filter.programId;
    }

    onClickFilterCallback(dataSet);
    previous = dataSet;
    if (reset) {
      setFrom(defaultFrom);
      setTo(defaultTo);
      setOnlyPending(false);
      setViewAll(false);
      setSelectedPrograms([]);
    }
  };

  const onClickFilterCallback = (values) => {
    let change = false;

    if (options.onlyPending) {
      if (!isEqual(values.onlyPending, previous.onlyPending)) {
        change = true;
      }
    }

    if (options.viewAll) {
      if (!isEqual(values.viewAll, previous.viewAll)) {
        change = true;
      }
    }

    if (options.programs) {
      if (!isEqual(values.programs, previous.programs)) {
        change = true;
      }
    }

    if (options.dateRange) {
      if (finalFilter.from !== values.from || finalFilter.to !== values.to) {
        change = true;
      }
    }

    if (!change) {
      alert("No change in filters");
      setUseFilter(false);
      return;
    }

    let filters = {};
    if (options.onlyPending) filters.onlyPending = values.onlyPending;
    if (options.viewAll) filters.viewAll = values.viewAll;
    if (options.programs) {
      filters.programs = values.programs;
    }
    if (options.dateRange) {
      filters.from = values.from;
      filters.to = values.to;
    }

    filters.programId = filter.programId;
    filters.programs = filter.programs;

    setFilter(filters);
    setUseFilter(true);
  };

  const onStartChange = (value) => {
    setFrom(value);
  };
  const onEndChange = (value) => {
    setTo(value);
  };

  const onChangeOnlyPending = () => {
    setOnlyPending(!onlyPending);
  };

  const onChangeViewAll = () => {
    setViewAll(!viewAll);
  };

  const cellStyle = { alignSelf: 'center' };

  return (
    <div className="mb-3" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 12, width: '100%' }}>
      {options.programs && (
        <div style={cellStyle}>
          <ProgramsHierarchy
            defaultPrograms={options.programs}
            selectedPrograms={selectedPrograms}
            setSelectedPrograms={setSelectedPrograms}
          />
        </div>
      )}
      {options.dateRange && (
        <>
          <div style={{ ...cellStyle, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>From</span>
            <DatePicker dateFormat="MM/dd/yyyy" selected={from} onChange={onStartChange} className="form-control form-control-sm" />
          </div>
          <div style={{ ...cellStyle, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>To</span>
            <DatePicker dateFormat="MM/dd/yyyy" selected={to} onChange={onEndChange} className="form-control form-control-sm" />
          </div>
        </>
      )}
      {options.onlyPending && (
        <div style={cellStyle}>
          <CheckBoxField
            name="onlyPending"
            label="Pending"
            checked={onlyPending}
            onChange={onChangeOnlyPending}
            type="checkbox"
          />
        </div>
      )}
      {options.viewAll && (
        <div style={cellStyle}>
          <CheckBoxField
            name="viewAll"
            label="View All"
            checked={viewAll}
            onChange={onChangeViewAll}
            type="checkbox"
          />
        </div>
      )}
      <div style={cellStyle}>
        <Button onClick={() => onClickFilter()} className="btn btn-sm btn-primary mr-2">Filter</Button>
        <Button onClick={() => onClickFilter(true)} className="btn btn-sm btn-primary">Reset</Button>
      </div>
      <div style={{ ...cellStyle, marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
        {allColumns.length > 0 && (
          <UncontrolledDropdown>
            <DropdownToggle caret className="btn btn-sm btn-outline-secondary">
              Toggle Columns
            </DropdownToggle>
            <DropdownMenu>
              {allColumns.map((column) => (
                <DropdownItem key={column.id} toggle={false}>
                  <input
                    type="checkbox"
                    {...column.getToggleHiddenProps()}
                    style={{ marginRight: 8 }}
                  />
                  <label>{column.Header}</label>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
        {options.exportToCsv && (
          <>
            <Button className="btn btn-sm btn-secondary" onClick={() => download(filter)}>Export to CSV</Button>
            <CSVLink
              data={exportData}
              headers={exportHeaders}
              filename={reportName ? reportName : 'report_schedule_awards_summary.csv'}
              className="hidden"
              ref={exportLink}
              target="_blank"
            />
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    organization: state.organization,
  };
};
export default connect(mapStateToProps)(ScheduleAwardsSummaryFilter);
